/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-03 15:48:53
 *
 *******************************************************/
// @flow

import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import CollectionAnalyticsOverview from 'src/components/containers/analytics-v2/collection-analytics/overview';
import CollectionPerformance from 'src/components/containers/analytics-v2/collection-analytics/collection-performance';
import FilterOptionValue from 'src/components/containers/analytics-v2/collection-analytics/filter-option-value';
import FilterOptionValueCombination from 'src/components/containers/analytics-v2/collection-analytics/filter-option-value-combination';

import { COLLECTION_ANALYTICS_TABS } from 'src/constants/analytics';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import WarningMetricIncompatible from './warning-metric-incompatible';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  pathname: string,
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;

class CollectionAnalyticsTabs extends Component<ComposedProps> {
  state = {
    selectedTab: 0
  };

  componentDidMount() {
    this.checkSelectedTab(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.checkSelectedTab(nextProps);
  }

  render() {
    const { i18n } = this.props;
    const { selectedTab } = this.state;
    const tabs = COLLECTION_ANALYTICS_TABS.map(tab => ({
      ...tab,
      content: i18n.translate(tab.content)
    }));

    return (
      <div className="BC-Analytics__Tabs">
        <div>
          <div className="Polaris-Tabs__Wrapper">
            <ul className="Polaris-Tabs" role="tablist">
              {tabs.map((item, index) => (
                <li
                  className="Polaris-Tabs__TabContainer"
                  key={index}
                  role="presentation"
                  onClick={() => this.handleChangeTab(index)}>
                  <button
                    className={`Polaris-Tabs__Tab ${
                      selectedTab === index ? 'Polaris-Tabs__Tab--selected' : ''
                    }`}
                    role="tab">
                    <span className="Polaris-Tabs__Title">{item.content}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <WarningMetricIncompatible />
          {this.renderCollectionAnalyticsTabContent()}
        </div>
      </div>
    );
  }

  checkSelectedTab = props => {
    const { pathname } = props;
    const tabIndex = COLLECTION_ANALYTICS_TABS.findIndex(tab => tab.link === pathname);
    this.setState({
      selectedTab: tabIndex
    });
  };

  renderCollectionAnalyticsTabContent = () => {
    const { pathname, performActionSurvey, openReviewAddonModal } = this.props;
    const Components = {
      [routes.analytics.overviewCollection()]: (
        <CollectionAnalyticsOverview
          openReviewAddonModal={openReviewAddonModal}
          performActionSurvey={performActionSurvey}
        />
      ),
      [routes.analytics.collectionPerformance()]: (
        <CollectionPerformance
          openReviewAddonModal={openReviewAddonModal}
          performActionSurvey={performActionSurvey}
        />
      ),
      [routes.analytics.filterOptionValue()]: (
        <FilterOptionValue
          openReviewAddonModal={openReviewAddonModal}
          performActionSurvey={performActionSurvey}
        />
      ),
      [routes.analytics.filterOptionValueCombination()]: (
        <FilterOptionValueCombination
          openReviewAddonModal={openReviewAddonModal}
          performActionSurvey={performActionSurvey}
        />
      )
    };
    return Components[pathname] ? Components[pathname] : null;
  };

  handleChangeTab = (tabIndex: number) => {
    const url = COLLECTION_ANALYTICS_TABS[tabIndex].link;
    onAbortApiAnalytic();
    history.push(url);
  };
}

export default withI18n()(CollectionAnalyticsTabs);
